/**
 * Step3SystemInfo Component
 *
 * Third step of assessment wizard - collects basic system information
 */

// System type definitions with labels
const SYSTEM_TYPE_LABELS = {
  centralAC: 'Central AC (Electric Cooling Only)',
  heatPump: 'Heat Pump (Electric Heating + Cooling)',
  heatPumpPackage: 'Heat Pump Package Unit (Electric Heating + Cooling)',
  furnaceAC: 'Gas Furnace + AC (Dual Fuel)',
  furnaceACPackage: 'Gas Furnace + AC Package Unit (Dual Fuel)',
  furnaceOnly: 'Gas Furnace Only',
  boiler: 'Boiler (Hydronic Heating)',
  ductlessMini: 'Ductless Mini-Split'
};

// Gas-dependent system types
const GAS_SYSTEM_TYPES = ['furnaceAC', 'furnaceACPackage', 'furnaceOnly', 'boiler'];

export default function Step3SystemInfo({ state, setField, errors }) {
  // Update display values when sliders change
  const updateAgeDisplay = (value) => {
    return `${value} years`;
  };

  const updateSqftDisplay = (value) => {
    return parseInt(value).toLocaleString() + ' sq ft';
  };

  // Get enabled system types from state
  // Empty array means no types are enabled (contractor must enable in Settings)
  const enabledSystemTypes = state.enabledSystemTypes || [];

  // Filter system types based on:
  // 1. Enabled in Settings
  // 2. Gas requirement (if applicable)
  const getAvailableSystemTypes = () => {
    return enabledSystemTypes.filter(systemType => {
      // If it's a gas system type, check if gas/propane is available
      if (GAS_SYSTEM_TYPES.includes(systemType)) {
        return state.hasGasOrPropane && state.gasProviderId;
      }
      // Non-gas systems are always available if enabled
      return true;
    });
  };

  const availableSystemTypes = getAvailableSystemTypes();

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '30px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}>
      <h2 style={{
        margin: '0 0 25px 0',
        color: '#2c3e50',
        fontSize: '24px'
      }}>
        Step 3: System Information
      </h2>

      {/* System Age */}
      <div style={{ marginBottom: '25px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: '600',
          color: '#2c3e50'
        }}>
          System Age: <span style={{ color: '#667eea', fontSize: '18px' }}>
            {updateAgeDisplay(state.systemAge)}
          </span>
        </label>
        <input
          type="range"
          min="0"
          max="25"
          value={state.systemAge}
          onChange={(e) => setField('systemAge', parseInt(e.target.value))}
          style={{
            width: '100%',
            height: '8px',
            borderRadius: '4px',
            outline: 'none',
            cursor: 'pointer'
          }}
        />
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '12px',
          color: '#64748b',
          marginTop: '5px'
        }}>
          <span>New</span>
          <span>25+ years</span>
        </div>
        {errors.systemAge && (
          <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '5px' }}>
            {errors.systemAge}
          </div>
        )}
      </div>

      {/* Repair Cost */}
      <div style={{ marginBottom: '25px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: '600',
          color: '#2c3e50'
        }}>
          Current Repair Cost
        </label>
        <div style={{ position: 'relative' }}>
          <span style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#64748b',
            fontSize: '16px'
          }}>
            $
          </span>
          <input
            type="text"
            inputMode="decimal"
            min="0"
            step="50"
            value={state.repairCost}
            onChange={(e) => {
              const value = e.target.value;
              // Allow empty string, numbers, and decimal point
              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                setField('repairCost', value === '' ? '' : value);
              }
            }}
            onBlur={(e) => {
              const value = e.target.value;
              const numValue = value === '' ? 0 : parseFloat(value);
              setField('repairCost', isNaN(numValue) ? 0 : numValue);
            }}
            style={{
              width: '100%',
              padding: '12px 12px 12px 30px',
              fontSize: '16px',
              border: errors.repairCost ? '2px solid #ef4444' : '2px solid #e0e6ed',
              borderRadius: '8px',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
          />
        </div>
        <small style={{ color: '#64748b', fontSize: '13px', display: 'block', marginTop: '5px' }}>
          Enter $0 if this is a maintenance visit or proactive assessment
        </small>
        {errors.repairCost && (
          <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '5px' }}>
            {errors.repairCost}
          </div>
        )}
      </div>

      {/* System Type */}
      <div style={{ marginBottom: '25px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: '600',
          color: '#2c3e50'
        }}>
          System Type
        </label>
        <select
          value={state.systemType}
          onChange={(e) => setField('systemType', e.target.value)}
          style={{
            width: '100%',
            padding: '12px',
            fontSize: '16px',
            border: errors.systemType ? '2px solid #ef4444' : '2px solid #e0e6ed',
            borderRadius: '8px',
            outline: 'none',
            cursor: 'pointer',
            background: 'white',
            transition: 'border-color 0.2s'
          }}
          onFocus={(e) => e.target.style.borderColor = '#667eea'}
          onBlur={(e) => e.target.style.borderColor = errors.systemType ? '#ef4444' : '#e0e6ed'}
        >
          {availableSystemTypes.length === 0 ? (
            <option value="">No system types enabled</option>
          ) : (
            <>
              <option value="">-- Select System Type --</option>
              {availableSystemTypes.map(systemType => (
                <option key={systemType} value={systemType}>
                  {SYSTEM_TYPE_LABELS[systemType]}
                </option>
              ))}
            </>
          )}
        </select>
        {errors.systemType && (
          <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '5px' }}>
            {errors.systemType}
          </div>
        )}

        {/* Show helper messages */}
        {availableSystemTypes.length === 0 && (
          <div style={{
            fontSize: '13px',
            color: '#dc2626',
            marginTop: '8px',
            padding: '10px 12px',
            background: '#fee2e2',
            borderRadius: '6px',
            border: '1px solid #fca5a5'
          }}>
            ❌ <strong>No system types available:</strong> Please enable system types in Settings → Systems before running assessments.
          </div>
        )}
        {!state.hasGasOrPropane && enabledSystemTypes.some(type => GAS_SYSTEM_TYPES.includes(type)) && (
          <div style={{
            fontSize: '13px',
            color: '#64748b',
            marginTop: '8px',
            padding: '10px 12px',
            background: '#f8fafc',
            borderRadius: '6px',
            border: '1px solid #e0e6ed'
          }}>
            💡 <strong>Note:</strong> Some system types are hidden because no natural gas or propane service was selected in Step 2.
          </div>
        )}
        {state.hasGasOrPropane && !state.gasProviderId && enabledSystemTypes.some(type => GAS_SYSTEM_TYPES.includes(type)) && (
          <div style={{
            fontSize: '13px',
            color: '#f59e0b',
            marginTop: '8px',
            padding: '10px 12px',
            background: '#fffbeb',
            borderRadius: '6px',
            border: '1px solid #fcd34d'
          }}>
            ⚠️ <strong>Gas provider required:</strong> Please select a gas/propane provider in Step 2 to access gas-dependent system types.
          </div>
        )}
      </div>

      {/* Square Footage */}
      <div style={{ marginBottom: '25px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontWeight: '600',
          color: '#2c3e50'
        }}>
          Square Footage: <span style={{ color: '#667eea', fontSize: '18px' }}>
            {updateSqftDisplay(state.squareFootage)}
          </span>
        </label>
        <input
          type="range"
          min="500"
          max="5000"
          step="100"
          value={state.squareFootage}
          onChange={(e) => setField('squareFootage', parseInt(e.target.value))}
          style={{
            width: '100%',
            height: '8px',
            borderRadius: '4px',
            outline: 'none',
            cursor: 'pointer'
          }}
        />
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '12px',
          color: '#64748b',
          marginTop: '5px'
        }}>
          <span>500 sq ft</span>
          <span>5,000 sq ft</span>
        </div>
        {errors.squareFootage && (
          <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '5px' }}>
            {errors.squareFootage}
          </div>
        )}
      </div>

      {/* Gas System Specific Fields */}
      {GAS_SYSTEM_TYPES.includes(state.systemType) && (
        <div style={{
          marginTop: '30px',
          padding: '20px',
          background: '#fffbeb',
          borderRadius: '10px',
          border: '2px solid #fbbf24'
        }}>
          <h3 style={{
            margin: '0 0 20px 0',
            color: '#92400e',
            fontSize: '18px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            🔥 Gas System Information
          </h3>

          {/* Current AFUE */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#2c3e50'
            }}>
              Current Furnace AFUE Rating (Optional)
            </label>
            <input
              type="number"
              min="60"
              max="98"
              step="1"
              value={state.current_afue || ''}
              placeholder="Leave blank to estimate from age"
              onChange={(e) => {
                const value = e.target.value === '' ? null : parseInt(e.target.value);
                setField('current_afue', value);
              }}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                border: errors.current_afue ? '2px solid #ef4444' : '2px solid #f59e0b',
                borderRadius: '8px',
                outline: 'none',
                background: 'white'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = errors.current_afue ? '#ef4444' : '#f59e0b'}
            />
            <small style={{ color: '#92400e', fontSize: '13px', display: 'block', marginTop: '5px' }}>
              💡 AFUE measures furnace efficiency (60-98%). Check the furnace nameplate or leave blank to estimate. This helps calculate accurate energy costs.
            </small>
            {errors.current_afue && (
              <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '5px' }}>
                {errors.current_afue}
              </div>
            )}
          </div>

          {/* Condensing Capability */}
          <div style={{ marginBottom: '0' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '600',
              color: '#2c3e50'
            }}>
              Can This Home Support a Condensing Furnace?
            </label>
            <select
              value={state.can_install_condensing || ''}
              onChange={(e) => setField('can_install_condensing', e.target.value || null)}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                border: errors.can_install_condensing ? '2px solid #ef4444' : '2px solid #f59e0b',
                borderRadius: '8px',
                outline: 'none',
                cursor: 'pointer',
                background: 'white'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = errors.can_install_condensing ? '#ef4444' : '#f59e0b'}
            >
              <option value="">-- Select --</option>
              <option value="yes">Yes - Can install condensing (90%+ AFUE)</option>
              <option value="no">No - Conventional only (80% AFUE)</option>
              <option value="unsure">Unsure - Show all options</option>
            </select>
            <div style={{
              marginTop: '10px',
              padding: '12px',
              background: 'white',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#92400e'
            }}>
              <strong>ℹ️ What's a condensing furnace?</strong>
              <ul style={{ margin: '8px 0 0 20px', paddingLeft: '0', lineHeight: '1.6' }}>
                <li><strong>Condensing (90%+ AFUE):</strong> High efficiency, requires PVC vent pipes to exterior wall. Not possible with all home layouts or code restrictions.</li>
                <li><strong>Conventional (80% AFUE):</strong> Standard efficiency, vents through existing chimney. Works with most homes.</li>
              </ul>
              <div style={{ marginTop: '8px', fontStyle: 'italic' }}>
                This determines which system options will be shown. Select "Unsure" to show all options and let the comfort consultant decide.
              </div>
            </div>
            {errors.can_install_condensing && (
              <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '5px' }}>
                {errors.can_install_condensing}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
